/**
 * @file app/page.tsx
 * @description Homepage with hero section and SEO content
 * Domain-specific content for bikeparkmapa.pl vs snowparkmap.com
 * @module app/page
 */

'use client';

import { useEffect, useState } from 'react';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import { Map, ClipboardList, Trophy, DollarSign, Star, Target } from 'lucide-react';

export default function HomePage() {
  const [isBikeMapka, setIsBikeMapka] = useState(true);
  const t = useTranslations('homepage');
  const tSEO = useTranslations('homepageSEO');

  // Helper to get raw HTML for dangerouslySetInnerHTML
  const tSEORaw = (key: string) => {
    try {
      return tSEO.raw(key) as string;
    } catch {
      return tSEO(key);
    }
  };

  useEffect(() => {
    const hostname = window.location.hostname;
    const searchParams = new URLSearchParams(window.location.search);
    const domainOverride = searchParams.get('domain');

    if (domainOverride === 'snowpark') {
      setIsBikeMapka(false);
    } else {
      setIsBikeMapka(hostname.includes('bikeparkmapa.pl') || hostname.includes('localhost'));
    }
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: t('jsonLd.name'),
    alternateName: t('jsonLd.alternateName'),
    url: 'https://bikeparkmapa.pl',
    description: t('jsonLd.description'),
    inLanguage: 'pl-PL',
    publisher: {
      '@type': 'Organization',
      name: 'BikeParkMapa.pl'
    },
    audience: {
      '@type': 'Audience',
      audienceType: ['Mountain bikers', 'Downhill riders', 'Enduro enthusiasts', 'MTB beginners']
    },
    about: {
      '@type': 'Thing',
      name: t('jsonLd.aboutName'),
      description: t('jsonLd.aboutDescription')
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://bikeparkmapa.pl/bikeparki?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: t('faq.q1.question'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.q1.answer')
        }
      },
      {
        '@type': 'Question',
        name: t('faq.q2.question'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.q2.answer')
        }
      },
      {
        '@type': 'Question',
        name: t('faq.q3.question'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.q3.answer')
        }
      },
      {
        '@type': 'Question',
        name: t('faq.q4.question'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.q4.answer')
        }
      },
      {
        '@type': 'Question',
        name: t('faq.q5.question'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.q5.answer')
        }
      },
      {
        '@type': 'Question',
        name: t('faq.q6.question'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('faq.q6.answer')
        }
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="homepage">
      {/* Hero Section with Image */}
      <section className="hero">
        <div className="hero-background">
          <div
            className="hero-image"
            role="img"
            aria-label={isBikeMapka ? t('heroImageAlt') : "Snowpark z przeszkodami freestyle"}
            style={{
              backgroundImage: isBikeMapka
                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.9), rgba(59, 130, 246, 0.8)), url(/api/placeholder/1920/800)'
                : 'linear-gradient(135deg, rgba(14, 165, 233, 0.9), rgba(6, 182, 212, 0.8)), url(/api/placeholder/1920/800)'
            }}
          ></div>
        </div>
        <div className="hero-content">
          {isBikeMapka ? (
            <>
              <h1 className="hero-title">
                {t('title')}
              </h1>
              <p className="hero-subtitle" dangerouslySetInnerHTML={{ __html: t('subtitle') }} />
            </>
          ) : (
            <>
              <h1 className="hero-title">
                Discover 200+ Best Snowparks in Europe
              </h1>
              <p className="hero-subtitle">
                Interactive map of world's best snowparks. From halfpipes to kickers and rails.
                Detailed info about features, prices, lifts and facilities.
              </p>
            </>
          )}
          <div className="hero-cta">
            <Link href={isBikeMapka ? '/bikeparki' : '/snowparks'} className="btn btn-primary">
              <span className="btn-icon"><Map size={20} /></span>
              {t('heroButton')}
            </Link>
            <Link href={isBikeMapka ? '/bikeparki/lista' : '/snowparks/lista'} className="btn btn-secondary">
              <span className="btn-icon"><ClipboardList size={20} /></span>
              {t('heroButtonList')}
            </Link>
            <Link href={isBikeMapka ? '/bikeparki/ranking' : '/snowparks/ranking'} className="btn btn-secondary">
              <span className="btn-icon"><Trophy size={20} /></span>
              {t('heroButtonRanking')}
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="features-container">
          <div className="feature-card">
            <div className="feature-icon"><Map size={48} strokeWidth={1.5} /></div>
            <h3>{t('featureMap')}</h3>
            <p>{t('featureMapDesc')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><DollarSign size={48} strokeWidth={1.5} /></div>
            <h3>{t('featurePrices')}</h3>
            <p>{t('featurePricesDesc')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><Star size={48} strokeWidth={1.5} /></div>
            <h3>{t('featureRatings')}</h3>
            <p>{t('featureRatingsDesc')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><Target size={48} strokeWidth={1.5} /></div>
            <h3>{t('featureFilters')}</h3>
            <p>{t('featureFiltersDesc')}</p>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="seo-content">
        <div className="seo-container">
          {isBikeMapka ? (
            <>
              <article className="guide-content">
                <h2 dangerouslySetInnerHTML={{ __html: tSEORaw('intro.heading') }} />
                <p dangerouslySetInnerHTML={{ __html: tSEORaw('intro.paragraph') }} />

                <h2>{tSEO('interaktywnaMapaBikeparkow.heading')}</h2>
                <p dangerouslySetInnerHTML={{ __html: tSEORaw('interaktywnaMapaBikeparkow.paragraph') }} />

                <h2>{tSEO('dlaczegoWarto.heading')}</h2>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaczegoWarto.list.bazaDanych') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaczegoWarto.list.porownanieCen') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaczegoWarto.list.recenzje') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaczegoWarto.list.filtry') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaczegoWarto.list.nawigacja') }} />
                </ul>

                <h2>{tSEO('najpopularniejszeBikeparki.heading')}</h2>
                <p>{tSEO('najpopularniejszeBikeparki.paragraph')}</p>

                <h2>{tSEO('dlaKogo.heading')}</h2>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaKogo.list.poczatkujacy') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaKogo.list.sredniozaawansowani') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaKogo.list.zaawansowani') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaKogo.list.rodziny') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('dlaKogo.list.planowanie') }} />
                </ul>

                <h2>{tSEO('czymJestBikepark.heading')}</h2>
                <p dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.paragraph1') }} />
                <p>{tSEO('czymJestBikepark.paragraph2')}</p>

                <h3>{tSEO('czymJestBikepark.infrastruktura.heading')}</h3>
                <p>{tSEO('czymJestBikepark.infrastruktura.intro')}</p>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.infrastruktura.list.wyciagi') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.infrastruktura.list.wypozyczalnie') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.infrastruktura.list.gastronomia') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.infrastruktura.list.parking') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.infrastruktura.list.spolecznosc') }} />
                </ul>

                <h3>{tSEO('czymJestBikepark.rodzajeTras.heading')}</h3>

                <div className="trail-type">
                  <h4>{tSEO('czymJestBikepark.rodzajeTras.flow.heading')}</h4>
                  <p>{tSEO('czymJestBikepark.rodzajeTras.flow.description')}</p>
                  <ul>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.flow.features.bermy')}</li>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.flow.features.rollers')}</li>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.flow.features.tableTops')}</li>
                  </ul>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.rodzajeTras.flow.level') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('czymJestBikepark.rodzajeTras.enduro.heading')}</h4>
                  <p>{tSEO('czymJestBikepark.rodzajeTras.enduro.description')}</p>
                  <ul>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.enduro.features.rockGardeny')}</li>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.enduro.features.dropy')}</li>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.enduro.features.zjazdy')}</li>
                  </ul>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.rodzajeTras.enduro.level') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('czymJestBikepark.rodzajeTras.downhill.heading')}</h4>
                  <p>{tSEO('czymJestBikepark.rodzajeTras.downhill.description')}</p>
                  <ul>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.downhill.features.duzeDropy')}</li>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.downhill.features.gap')}</li>
                    <li>{tSEO('czymJestBikepark.rodzajeTras.downhill.features.rockGardeny')}</li>
                  </ul>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.rodzajeTras.downhill.level') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('czymJestBikepark.rodzajeTras.pumptrack.heading')}</h4>
                  <p>{tSEO('czymJestBikepark.rodzajeTras.pumptrack.description')}</p>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.rodzajeTras.pumptrack.level') }} />
                </div>

                <h3>{tSEO('czymJestBikepark.oznaczeniaTras.heading')}</h3>
                <ul className="trail-colors">
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.oznaczeniaTras.list.zielona') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.oznaczeniaTras.list.niebieska') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.oznaczeniaTras.list.czerwona') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('czymJestBikepark.oznaczeniaTras.list.czarna') }} />
                </ul>

                <h2>{tSEO('najlepszeBikeparki.heading')}</h2>
                <p>{tSEO('najlepszeBikeparki.intro')}</p>

                <div className="trail-type highlight-park">
                  <div className="best-badge">{tSEO('najlepszeBikeparki.tigerAirlines.badge')}</div>
                  <h4>{tSEO('najlepszeBikeparki.tigerAirlines.heading')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.description') }} />
                  <ul>
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.features.branding') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.features.trasy') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.features.wyciag') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.features.polozenie') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.features.infrastruktura') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.features.cena') }} />
                  </ul>
                  <p className="park-verdict" dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.tigerAirlines.verdict') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('najlepszeBikeparki.kasinaSki.heading')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.kasinaSki.description') }} />
                  <ul>
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.kasinaSki.features.poczatkujacy') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.kasinaSki.features.wyciag') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.kasinaSki.features.trasy') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.kasinaSki.features.szkola') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.kasinaSki.features.zawody') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.kasinaSki.features.ceny') }} />
                  </ul>
                </div>

                <div className="trail-type">
                  <h4>{tSEO('najlepszeBikeparki.slotwiny.heading')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.description') }} />
                  <ul>
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.features.family') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.features.trasy') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.features.szkola') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.features.polozenie') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.features.ceny') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.features.wypozyczalnia') }} />
                  </ul>
                  <p className="park-verdict" dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.slotwiny.verdict') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('najlepszeBikeparki.czarnaGora.heading')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.czarnaGora.description') }} />
                  <ul>
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.czarnaGora.features.trasy') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.czarnaGora.features.techniczne') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.czarnaGora.features.zawody') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('najlepszeBikeparki.czarnaGora.features.wyciagi') }} />
                  </ul>
                </div>

                <div className="trail-type">
                  <h4>{tSEO('najlepszeBikeparki.szczyrk.heading')}</h4>
                  <p>{tSEO('najlepszeBikeparki.szczyrk.description')}</p>
                </div>

                <div className="trail-type">
                  <h4>{tSEO('najlepszeBikeparki.joyRide.heading')}</h4>
                  <p>{tSEO('najlepszeBikeparki.joyRide.description')}</p>
                </div>

                <div className="trail-type">
                  <h4>{tSEO('najlepszeBikeparki.mosorny.heading')}</h4>
                  <p>{tSEO('najlepszeBikeparki.mosorny.description')}</p>
                </div>

                <h2>{tSEO('jakWybrac.heading')}</h2>
                <p>{tSEO('jakWybrac.intro')}</p>

                <h3>{tSEO('jakWybrac.poczatkujacy.heading')}</h3>
                <p dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.intro') }} />
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.list.slotwiny') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.list.kasinaSki') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.list.zieleniec') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.list.szkolki') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.list.wypozyczalnia') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.list.inne') }} />
                </ul>
                <p className="beginner-tip" dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.poczatkujacy.tip') }} />

                <h3>{tSEO('jakWybrac.sredniozaawansowani.heading')}</h3>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.sredniozaawansowani.list.trasy') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.sredniozaawansowani.list.singletracki') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.sredniozaawansowani.list.mix') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.sredniozaawansowani.list.najlepsze') }} />
                </ul>

                <h3>{tSEO('jakWybrac.zaawansowani.heading')}</h3>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.zaawansowani.list.czarne') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.zaawansowani.list.zawody') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.zaawansowani.list.setup') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('jakWybrac.zaawansowani.list.najlepsze') }} />
                </ul>

                <h2>{tSEO('odCzegoZaczac.heading')}</h2>

                <h3>{tSEO('odCzegoZaczac.rower.heading')}</h3>
                <p dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.rower.intro') }} />
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.rower.list.hardtail') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.rower.list.trail') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.rower.list.wypozyczalnia') }} />
                </ul>

                <h3>{tSEO('odCzegoZaczac.ochrona.heading')}</h3>
                <ul className="trail-colors">
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.ochrona.list.kask') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.ochrona.list.ochraniacze') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.ochrona.list.rekawiczki') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.ochrona.list.okulary') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.ochrona.list.protektory') }} />
                </ul>

                <h3>{tSEO('odCzegoZaczac.pierwszaWizyta.heading')}</h3>
                <ol>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.pierwszaWizyta.steps.step1') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.pierwszaWizyta.steps.step2') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.pierwszaWizyta.steps.step3') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.pierwszaWizyta.steps.step4') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('odCzegoZaczac.pierwszaWizyta.steps.step5') }} />
                </ol>

                <h2>{tSEO('cenyIKoszty.heading')}</h2>
                <p>{tSEO('cenyIKoszty.intro')}</p>

                <h3>{tSEO('cenyIKoszty.karnety.heading')}</h3>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.karnety.list.4h') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.karnety.list.calodniowy') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.karnety.list.weekend') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.karnety.list.sezonowy') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.karnety.list.dzieci') }} />
                </ul>

                <h3>{tSEO('cenyIKoszty.wypozyczalnia.heading')}</h3>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.wypozyczalnia.list.trail') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.wypozyczalnia.list.dh') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.wypozyczalnia.list.ochraniacze') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.wypozyczalnia.list.kask') }} />
                </ul>

                <h3>{tSEO('cenyIKoszty.dodatkoweKoszty.heading')}</h3>
                <ul>
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.dodatkoweKoszty.list.wyzywienie') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.dodatkoweKoszty.list.nocleg') }} />
                  <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.dodatkoweKoszty.list.dojazd') }} />
                </ul>

                <div className="trail-type">
                  <h4>{tSEO('cenyIKoszty.proTip.heading')}</h4>
                  <ul>
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.proTip.list.online') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.proTip.list.powszednie') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.proTip.list.grupy') }} />
                    <li dangerouslySetInnerHTML={{ __html: tSEORaw('cenyIKoszty.proTip.list.wielokrotne') }} />
                  </ul>
                </div>

                <h2>{tSEO('sezonRowerowy.heading')}</h2>

                <h3>{tSEO('sezonRowerowy.maj.heading')}</h3>
                <p>
                  <span dangerouslySetInnerHTML={{ __html: tSEORaw('sezonRowerowy.maj.plusy') }} /><br/>
                  <span dangerouslySetInnerHTML={{ __html: tSEORaw('sezonRowerowy.maj.minusy') }} />
                </p>

                <h3>{tSEO('sezonRowerowy.lato.heading')}</h3>
                <p>
                  <span dangerouslySetInnerHTML={{ __html: tSEORaw('sezonRowerowy.lato.plusy') }} /><br/>
                  <span dangerouslySetInnerHTML={{ __html: tSEORaw('sezonRowerowy.lato.minusy') }} />
                </p>

                <h3>{tSEO('sezonRowerowy.jesien.heading')}</h3>
                <p>
                  <span dangerouslySetInnerHTML={{ __html: tSEORaw('sezonRowerowy.jesien.plusy') }} /><br/>
                  <span dangerouslySetInnerHTML={{ __html: tSEORaw('sezonRowerowy.jesien.minusy') }} />
                </p>

                <h3>{tSEO('sezonRowerowy.zima.heading')}</h3>
                <p>{tSEO('sezonRowerowy.zima.opis')}</p>

                <h2>{tSEO('faq.heading')}</h2>

                <div className="trail-type">
                  <h4>{tSEO('faq.wlasnyRower.pytanie')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('faq.wlasnyRower.odpowiedz') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('faq.fullSuspension.pytanie')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('faq.fullSuspension.odpowiedz') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('faq.wiek.pytanie')}</h4>
                  <p>{tSEO('faq.wiek.odpowiedz')}</p>
                </div>

                <div className="trail-type">
                  <h4>{tSEO('faq.doswiadczenie.pytanie')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('faq.doswiadczenie.odpowiedz') }} />
                </div>

                <div className="trail-type">
                  <h4>{tSEO('faq.zjazd.pytanie')}</h4>
                  <p>{tSEO('faq.zjazd.odpowiedz')}</p>
                </div>

                <div className="trail-type">
                  <h4>{tSEO('faq.deszcz.pytanie')}</h4>
                  <p dangerouslySetInnerHTML={{ __html: tSEORaw('faq.deszcz.odpowiedz') }} />
                </div>

                <h2>{tSEO('cta.heading')}</h2>
                <p>{tSEO('cta.paragraph')}</p>

              </article>
            </>
          ) : (
            <>
              <article className="guide-content">
                <h2>Discover 200+ Best Snowparks in Europe and Worldwide</h2>
                <p>
                  Find over <strong>200 best snowparks in Europe and worldwide</strong> on our interactive map. From legendary <strong>Snowpark Laax</strong> in Switzerland with world's largest halfpipe, through <strong>Absolut Park</strong> in Austria with 100+ features, to Japan's <strong>Hakuba 47</strong> - discover the perfect park for your level and riding style.
                </p>
                <p>
                  Compare lift pass prices, check available features, rate conditions and plan your next freestyle session.
                </p>

                <h3>What You'll Find on the Map</h3>
                <ul>
                  <li><strong>200+ Snowparks in 25 Countries</strong> - From Alps to Scandinavia, Japan to USA - the most complete snowpark map</li>
                  <li><strong>Lift Pass Price Comparison</strong> - Prices from 20€ (Poland, Czechia) to 85€ (St. Moritz)</li>
                  <li><strong>Rankings & User Reviews</strong> - Verified ratings, current conditions from the community</li>
                  <li><strong>Advanced Filters</strong> - Search parks with halfpipe, big kickers, rail gardens, jib zones</li>
                  <li><strong>Interactive GPS Map</strong> - Precise locations, navigation, directions. Save favorites</li>
                </ul>

                <h3>Top Snowpark Regions</h3>

                <div className="trail-type">
                  <h4>Austria - European Freestyle Mecca</h4>
                  <p>Austria is the <strong>absolute freestyle leader in Europe</strong> with over 50 world-class snowparks. The country offers the highest concentration of parks globally, professional shaping by <strong>QParks</strong> and access to huge skipass systems like <strong>Ski Amadé</strong> (760km slopes, 25 snowparks).</p>
                  <ul>
                    <li><strong>Absolut Park Flachauwinkl</strong> - Europe's largest (1,500m, 100+ features)</li>
                    <li><strong>Kings Park am Hochkönig</strong> - 1,800m pure freestyle (67 elements)</li>
                    <li><strong>Kitzsteinhorn SuperPipe</strong> - Austria's largest halfpipe (6.5m)</li>
                  </ul>
                  <p><strong>Prices:</strong> 54€ - 74€ per day | <strong>Season:</strong> October - May (glacier parks year-round)</p>
                </div>

                <div className="trail-type">
                  <h4>Switzerland - Precision and Innovation</h4>
                  <p>Swiss snowparks are <strong>synonymous with highest quality</strong>. Perfect shaping, innovative features, spectacular views of 4000m peaks and long season on glaciers.</p>
                  <ul>
                    <li><strong>Snowpark Laax</strong> - Europe's Freestyle Mecca (90+ features, world's largest halfpipe 200m × 6.9m)</li>
                    <li><strong>Zermatt Snowpark</strong> - highest in Alps (3,250m, 300+ riding days)</li>
                    <li><strong>Snowpark Davos Jakobshorn</strong> - legendary night sessions in illuminated halfpipe</li>
                  </ul>
                  <p><strong>Prices:</strong> 65€ - 85€ per day | <strong>Events:</strong> LAAX OPEN, Swatch Nines</p>
                </div>

                <div className="trail-type">
                  <h4>France - Glacier Riding & Style</h4>
                  <p>French snowparks combine <strong>high-quality shaping</strong> with longest season in Alps thanks to glacier parks. From Les 2 Alpes to Val d'Isère - where freestyle legends are born.</p>
                  <ul>
                    <li><strong>Les 2 Alpes Snowpark</strong> - Europe's biggest park, year-round on glacier</li>
                    <li><strong>DC Park Tignes</strong> - 2,400-2,600m, summer & winter riding</li>
                    <li><strong>Val Thorens Snowpark</strong> - Europe's highest resort (2,300m)</li>
                  </ul>
                  <p><strong>Prices:</strong> 48€ - 70€ per day | <strong>Bonus:</strong> 300 sunny days annually</p>
                </div>

                <div className="trail-type">
                  <h4>Best Halfpipe Parks</h4>
                  <p>Looking for a real halfpipe with 6m+ walls? Check out top parks:</p>
                  <ul className="trail-colors">
                    <li>1. <strong>Laax</strong> (Switzerland) - World's Largest (200m × 6.9m)</li>
                    <li>2. <strong>Absolut Park</strong> (Austria) - 100m SuperPipe</li>
                    <li>3. <strong>Kitzsteinhorn</strong> (Austria) - Austria's Largest (6.5m)</li>
                    <li>4. <strong>Davos Jakobshorn</strong> (Switzerland) - 2× Halfpipes + Night Sessions</li>
                    <li>5. <strong>Mammoth Mountain</strong> (USA) - 2× Olympic-size pipes</li>
                  </ul>
                </div>
              </article>
            </>
          )}
        </div>
      </section>

    </div>
    </>
  );
}

